import React, { useRef } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import { FieldCheckbox } from '../../../components';
import SelectSingleFilter from '../SelectSingleFilter/SelectSingleFilter';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectMultipleFilter.module.css';

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options } = props;
  const sliderRef = useRef();
  const isTag = id.includes('tag');

  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const renderFilterItem = optionConfig => {
    const { option, label, component: CustomComponent } = optionConfig;
    const fieldId = `${id}.${option}`;

    if (CustomComponent) {
      return (
        <li key={fieldId} className={css.pillItem}>
          <CustomComponent
            id={fieldId}
            name={name}
            label={label}
            value={option}
            className={css.pill}
          />
        </li>
      );
    }

    return (
      <li key={fieldId} className={css.pillItem}>
        <FieldCheckbox
          id={fieldId}
          name={name}
          label={label}
          value={option}
          rootClassName={css.pill}
          hideIcon={true}
        />
      </li>
    );
  };

  if (isTag) {
    return (
      <div className={css.sliderOuter}>
        <div className={css.sliderContainer} ref={sliderRef}>
          <ul className={css.sliderList}>
            {options.map(renderFilterItem)}
          </ul>
          <div className={css.fadeContainer}></div>
        </div>
      </div>
    );
  } else {
    return (
      <div className={className}>
        <ul className={css.list}>
          {options.map(optionConfig => {
            const { option, label, component: CustomComponent } = optionConfig;
            const fieldId = `${id}.${option}`;

            if (CustomComponent) {
              return (
                <li key={fieldId} className={css.checkboxItem}>
                  <CustomComponent
                    id={fieldId}
                    name={name}
                    label={label}
                    value={option}
                    className={css.checkbox}
                  />
                </li>
              );
            }

            return (
              <li key={fieldId} className={css.checkboxItem}>
                <FieldCheckbox
                  id={fieldId}
                  name={name}
                  label={label}
                  value={option}
                  rootClassName={css.checkbox}
                />
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

/**
 * SelectMultipleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {node} props.label - The label
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {Array<Object>} props.options - The options array where each option can have:
 *   - option: string (required) - The option value
 *   - label: string (required) - The display label
 *   - component: React.Component (optional) - Custom component to render instead of default FieldCheckbox
 * @param {SCHEMA_TYPE_ENUM | SCHEMA_TYPE_MULTI_ENUM} props.schemaType - The schema type
 * @param {'has_all' | 'has_any'} props.searchMode - The search mode
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @returns {JSX.Element}
 */
const SelectMultipleFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsPopup,
    ...rest
  } = props;

  // Check if this is a category filter (categoryLevel, not main_kategori)
  // Only categoryLevel should use SelectSingleFilter, not other filters with "kategori" in the name
  const isCategory = id.includes('categorylevel') || id.includes('category-level');

  // If it's a category filter, render SelectSingleFilter instead
  if (isCategory) {
    return (
      <SelectSingleFilter
        rootClassName={rootClassName}
        className={className}
        id={id}
        name={name}
        label={label}
        queryParamNames={queryParamNames}
        options={options}
        initialValues={initialValues}
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={onSubmit}
        showAsPopup={showAsPopup}
        {...rest}
      />
    );
  }

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
  const selectedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilter.labelSelected' },
        { labelText: label, count: selectedOptions.length }
      )
    : label;

  const labelSelectionForPlain = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilterPlainForm.labelSelected' },
        { count: selectedOptions.length }
      )
    : '';

  // pass the initial values with the name key so that
  // they can be passed to the correct field
  const namedInitialValues = { [name]: selectedOptions };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={labelForPopup}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroup}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      labelSelection={labelSelectionForPlain}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroupPlain}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
      />
    </FilterPlain>
  );
};

export default SelectMultipleFilter;
